import { User, UserManager } from "oidc-client-ts";
import type { UserManagerSettings } from "oidc-client-ts";
import { teacherOidcConfig, studentOidcConfig } from "./oidcConfig";

export class AuthService {
  private userManager: UserManager;

  constructor(config?: UserManagerSettings) {
    if (config) {
      this.userManager = new UserManager(config);
    } else {
      // 採用英文版本的簡單邏輯：包含 student 或首頁用學生配置，其他用教師配置
      const config = location.pathname.includes("student") || location.pathname === "/"
        ? studentOidcConfig
        : teacherOidcConfig;
      this.userManager = new UserManager(config);
    }
  }

  private getUserManager(): UserManager {
    return this.userManager;
  }

  // 登入 - 添加角色參數
  public async signIn(role?: "teacher" | "student"): Promise<void> {
    try {
      // 如果指定了角色，存儲到 sessionStorage
      if (role) {
        sessionStorage.setItem("userRole", role);
      }
      
      const userManager = this.getUserManager();
      await userManager.signinRedirect();
    } catch (error) {
      console.error("登入失敗:", error);
      throw error;
    }
  }

  // 處理登入回調
  public async handleSigninCallback(): Promise<User> {
    try {
      const userManager = this.getUserManager();
      const user = await userManager.signinRedirectCallback();
      
      // 檢查 profile.name 是否為 UUID 格式
      if (user && user.profile) {
        // 優先使用 firstName + lastName
        if (user.profile.firstName && user.profile.lastName) {
          user.profile.name = `${user.profile.lastName}${user.profile.firstName}`;
        } else {
          const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(user.profile.name || '');

          // 如果 name 是 UUID，嘗試使用其他欄位
          if (isUUID && user.profile.preferred_username) {
            user.profile.name = user.profile.preferred_username;
          }

          // 處理 name 為陣列的情況
          if (Array.isArray(user.profile.name)) {
            user.profile.name = user.profile.name[0] || "用戶";
          }
        }
      }
      
      return user;
    } catch (error) {
      console.error("登入回調處理失敗:", error);
      throw error;
    }
  }

  // 登出
  public async signOut(): Promise<void> {
    try {
      // 檢查當前用戶角色來決定使用哪個配置
      const userDataString = localStorage.getItem("oidc.user:https://id.hle.com.tw:js");
      let userRole = "學生"; // 預設為學生
      
      if (userDataString) {
        try {
          const userData = JSON.parse(userDataString);
          userRole = userData.profile?.role || userData.profile?.['custom:role'] || "學生";
        } catch (error) {
          console.error("解析用戶資料失敗:", error);
        }
      }
      
      // 根據用戶角色創建對應的 UserManager
      const config = userRole === "老師" ? teacherOidcConfig : studentOidcConfig;
      const userManager = new UserManager(config);
      
      await userManager.signoutRedirect();
      // 清除角色信息
      sessionStorage.removeItem("userRole");
    } catch (error) {
      console.error("登出失敗:", error);
      throw error;
    }
  }

  // 取得目前使用者
  public async getUser(): Promise<User | null> {
    try {
      const userManager = this.getUserManager();
      const user = await userManager.getUser();
      
      // 檢查並處理 UUID 名稱問題
      if (user && user.profile) {
        // 優先使用 firstName + lastName
        if (user.profile.firstName && user.profile.lastName) {
          user.profile.name = `${user.profile.lastName}${user.profile.firstName}`;
        } else {
          const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(user.profile.name || '');

          // 如果 name 是 UUID，嘗試使用其他欄位
          if (isUUID && user.profile.preferred_username) {
            user.profile.name = user.profile.preferred_username;
          }

          // 處理 name 為陣列的情況
          if (Array.isArray(user.profile.name)) {
            user.profile.name = user.profile.name[0] || "用戶";
          }
        }
      }
      
      return user;
    } catch (error) {
      console.error("取得使用者失敗:", error);
      return null;
    }
  }

  // 檢查是否已登入
  public async isAuthenticated(): Promise<boolean> {
    const user = await this.getUser();
    return user !== null && !user.expired;
  }

  // 靜默續約
  public async renewToken(): Promise<User | null> {
    try {
      const userManager = this.getUserManager();
      return await userManager.signinSilent();
    } catch (error) {
      console.error("Token 續約失敗:", error);
      throw error;
    }
  }

  // 處理靜默回調
  public async handleSilentCallback(): Promise<void> {
    try {
      const userManager = this.getUserManager();
      await userManager.signinSilentCallback();
    } catch (error) {
      console.error("靜默回調處理失敗:", error);
      throw error;
    }
  }
}

// 採用英文版本的簡單方法：單一 authService 實例
export const authService = new AuthService();